import React from "react";
import { FiActivity, FiFileText, FiShield } from "react-icons/fi";
import dayjs from "newui/utils/dayjs";
import {
	AuditActionCategory,
	AuditLogResource,
	formatAuditContextTypeLabel,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface AuditTrailTableProps {
	events: AuditLogResource[];
	isLoading: boolean;
	onSelectEvent: (event: AuditLogResource) => void;
}

const CATEGORY_ICON: Record<AuditActionCategory, React.ReactElement> = {
	security: <FiShield size={16} className="text-red-500" />,
	rfx_mutation: <FiFileText size={16} className="text-blue-500" />,
	general: <FiActivity size={16} className="text-gray-500" />,
};

const CATEGORY_LABEL: Record<AuditActionCategory, string> = {
	security: "Security",
	rfx_mutation: "RFx Change",
	general: "General",
};

const COLUMNS = ["Type", "Name / Description", "Timestamp", "Actor"];

const SkeletonRow = () => (
	<tr className="border-b border-gray-200">
		{COLUMNS.map((col) => (
			<td key={col} className="px-4 py-3">
				<div className="h-4 rounded bg-gray-200 animate-pulse" />
			</td>
		))}
	</tr>
);

const EmptyState = () => (
	<tr>
		<td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-sm text-gray-500">
			No audit events found for the selected filters.
		</td>
	</tr>
);

export const AuditTrailTable = ({ events, isLoading, onSelectEvent }: AuditTrailTableProps) => {
	return (
		<div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
			<table data-testid="audit-trail-table" className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						{COLUMNS.map((col) => (
							<th
								key={col}
								className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap"
							>
								{col}
							</th>
						))}
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-200 bg-white">
					{isLoading ? (
						<>
							<SkeletonRow />
							<SkeletonRow />
							<SkeletonRow />
						</>
					) : events.length === 0 ? (
						<EmptyState />
					) : (
						events.map((event) => (
							<tr
								key={event.id}
								data-testid={`audit-row-${event.id}`}
								role="button"
								tabIndex={0}
								onClick={() => onSelectEvent(event)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										onSelectEvent(event);
									}
								}}
								className="cursor-pointer hover:bg-gray-50"
							>
								<td className="whitespace-nowrap px-4 py-3">
									<div className="flex items-center gap-2">
										{CATEGORY_ICON[event.action_category]}
										<span className="text-sm text-gray-700">{CATEGORY_LABEL[event.action_category]}</span>
									</div>
								</td>
								<td className="px-4 py-3">
									<div className="text-sm font-medium text-gray-900">{event.action}</div>
									<div className="text-xs text-gray-500">
										{formatAuditContextTypeLabel(event.context_type)}
										{event.context_id !== null ? ` #${event.context_id}` : ""}
									</div>
								</td>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
									{dayjs(event.created_at).format("DD MMM YYYY, h:mm A")}
								</td>
								<td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
									{event.actor?.name ?? "System"}
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);
};
